import React from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Card,
  CardContent,
  Grid,
  CssBaseline,
  Typography,
  Container,
  IconButton,
} from '@mui/material';
import Copyright from '../components/Copyright';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from '@mui/styles';

import BackButtonIcon from '../assets/Icon/BackButtonIcon.png';
import FarmAppLogo from '../assets/logo/FarmAppLogo.png';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';

const useStyles = makeStyles(() => ({
  root: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    padding: '0 !important',
  },
  container: {
    padding: '20px !important',
    minHeight: '100vh',
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px !important',
  },
  logo: {
    width: '150px',
    height: 'auto',
    marginBottom: '20px !important',
  },
  formCard: {
    borderRadius: '15px !important',
    boxShadow: '0 10px 30px rgba(0,0,0,0.1) !important',
    background: 'white !important',
    marginBottom: '20px !important',
  },
  contactCard: {
    padding: '20px !important',
    borderRadius: '15px !important',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important',
    color: 'white !important',
    marginBottom: '20px !important',
  },
  contactItem: {
    display: 'flex !important',
    alignItems: 'center !important',
    padding: '15px !important',
    borderRadius: '10px !important',
    background: 'rgba(255,255,255,0.1) !important',
    marginBottom: '15px !important',
    cursor: 'pointer !important',
    transition: 'all 0.3s ease !important',
    '&:hover': {
      background: 'rgba(255,255,255,0.2) !important',
      transform: 'translateY(-2px) !important',
    },
  },
  submitButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important',
    color: 'white !important',
    padding: '12px 30px !important',
    borderRadius: '25px !important',
    fontSize: '16px !important',
    fontWeight: '600 !important',
    textTransform: 'none !important',
    '&:hover': {
      background:
        'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%) !important',
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px !important',
    },
  },
}));

const Enquiry = () => {
  const classes = useStyles();
  const navigate = useNavigate();

  return (
    <Box className={classes.root}>
      <Helmet>
        <title>Enquiry - Farmapp</title>
      </Helmet>

      <Container component="main" maxWidth="md" className={classes.container}>
        <CssBaseline />

        {/* Header */}
        <Box className={classes.header}>
          <IconButton
            edge="start"
            sx={{ position: 'absolute', left: 20, top: 20 }}
            aria-label="back"
            onClick={() => navigate(-1)}
          >
            <img src={BackButtonIcon} alt="Back" />
          </IconButton>

          <img alt="FarmApp Logo" src={FarmAppLogo} className={classes.logo} />

          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#333',
              marginBottom: 1,
            }}
          >
            Get In Touch
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#666',
              fontSize: '1.1rem',
            }}
          >
            We&apos;d love to hear from you. Send us a message and we&apos;ll
            respond as soon as possible.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Enquiry Form - Priority Section */}
          <Grid item xs={12} lg={8}>
            <Card className={classes.formCard}>
              <CardContent>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    marginBottom: 3,
                    color: '#333',
                  }}
                >
                  Submit Your Enquiry
                </Typography>

                <Box
                  sx={{
                    width: '100%',
                    height: '600px',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <iframe
                    src="https://docs.google.com/forms/d/e/1FAIpQLSd9-HOgwQaGHsvsphIzRZHNU89petB4fUJ6sGJM2kO2wB1dkA/viewform?embedded=true"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    marginHeight="0"
                    marginWidth="0"
                    title="Enquiry Form"
                    style={{
                      border: 'none',
                      borderRadius: '10px',
                    }}
                  >
                    Loading…
                  </iframe>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} lg={4}>
            <Card className={classes.contactCard}>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    marginBottom: 3,
                    textAlign: 'center',
                  }}
                >
                  Contact Information
                </Typography>

                <Box
                  className={classes.contactItem}
                  onClick={() => window.open('tel:+919372186859')}
                >
                  <PhoneIcon sx={{ mr: 2, fontSize: 24 }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      +91-9372186859
                    </Typography>
                    <Typography variant="caption">Call us directly</Typography>
                  </Box>
                </Box>
                <Box
                  className={classes.contactItem}
                  onClick={() =>
                    window.open('https://wa.me/919372186859?text=Hi')
                  }
                >
                  <WhatsAppIcon sx={{ mr: 2, fontSize: 24 }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      +91-9372186859
                    </Typography>
                    <Typography variant="caption">
                      Chat with us on WhatsApp
                    </Typography>
                  </Box>
                </Box>

                <Box
                  className={classes.contactItem}
                  onClick={() => window.open('mailto:enquiry@asqi.in')}
                >
                  <EmailIcon sx={{ mr: 2, fontSize: 24 }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      enquiry@asqi.in
                    </Typography>
                    <Typography variant="caption">Send us an email</Typography>
                  </Box>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'center',
                    mt: 3,
                    opacity: 0.9,
                  }}
                >
                  Our team typically responds within 24 hours
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box mt={4}>
          <Copyright />
        </Box>
      </Container>
    </Box>
  );
};

export default Enquiry;
